// src/pages/ServicesPage.js
// จัดการบริการ + หมวดหมู่ + รูปภาพ + เปิด/ปิดใช้งานด้วย is_active
// UI ปรับใหม่: Single Column Layout

import React, { useContext, useEffect, useMemo, useState } from "react";
import api from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import ServiceCategoryManagerModal from "../components/ServiceCategoryManagerModal";

const ServicesPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const isAdmin = user?.role === "admin";

  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // ฟอร์มเพิ่มหมวดหมู่
  const [catName, setCatName] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  // ฟอร์มเพิ่ม/แก้ไขบริการ
  const initialServiceForm = {
    service_id: null,
    service_name: "",
    price: "",
    commission_rate: "",
    item_type: "service",
    category_id: "",
    image_url: "",
    barcode: "",
    is_active: true,
  };
  const [serviceForm, setServiceForm] = useState(initialServiceForm);
  const [isEditingService, setIsEditingService] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);

  // filter แสดงบริการ
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Category pagination
  const [categoryPage, setCategoryPage] = useState(1);
  const categoryPerPage = 5;

  // Extra filters & sorting
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const LOW_STOCK = 5;

  // Quick stock adjust
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [stockTarget, setStockTarget] = useState(null);
  const [stockInput, setStockInput] = useState('');

  // โหลดข้อมูลเริ่มต้น
  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, svcRes] = await Promise.all([
        api.get("/service-categories?active_only=true"),
        api.get("/services"),
      ]);
      setCategories(catRes.data || []);
      setServices(svcRes.data || []);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดข้อมูลบริการ/หมวดหมู่ไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatMoney = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const resetServiceForm = () => {
    setServiceForm(initialServiceForm);
    setIsEditingService(false);
    setIsServiceModalOpen(false);
  };

  // หมวดหมู่บริการ
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!catName.trim()) {
      addNotification("กรุณากรอกชื่อหมวดหมู่", "warning");
      return;
    }
    try {
      const payload = { name: catName.trim(), description: catDesc || null };
      if (editingCategoryId) {
        const res = await api.put(`/service-categories/${editingCategoryId}`, payload);
        setCategories((prev) =>
          prev.map((c) => (c.id === editingCategoryId ? res.data : c))
        );
        addNotification("แก้ไขหมวดหมู่สำเร็จ", "success");
      } else {
        const res = await api.post("/service-categories", payload);
        setCategories((prev) => [...prev, res.data]);
        addNotification("เพิ่มหมวดหมู่สำเร็จ", "success");
      }
      resetCategoryForm();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกข้อมูลหมวดหมู่ไม่สำเร็จ",
        "danger"
      );
    }
  };

  const resetCategoryForm = () => {
    setCatName("");
    setCatDesc("");
    setEditingCategoryId(null);
    setIsCategoryModalOpen(false);
  };

  const handleDeleteCategory = async (cat) => {
    if (!isAdmin) return;
    if (
      !window.confirm(
        `ต้องการลบหมวดหมู่ "${cat.name}" หรือไม่?\n(ถ้ามีบริการผูกอยู่ควรตรวจสอบก่อน)`
      )
    )
      return;
    try {
      await api.delete(`/service-categories/${cat.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      addNotification("ลบหมวดหมู่สำเร็จ", "success");
    } catch (err) {
      addNotification(
        err.response?.data?.message || "ลบหมวดหมู่ไม่สำเร็จ",
        "danger"
      );
    }
  };

  // ฟอร์มบริการ
  const handleServiceFieldChange = (field, value) => {
    setServiceForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const openStockModal = (svc) => {
    setStockTarget(svc);
    setStockInput(String(svc.stock_qty ?? 0));
    setIsStockModalOpen(true);
  };

  const closeStockModal = () => {
    setIsStockModalOpen(false);
    setStockTarget(null);
    setStockInput('');
  };

  const handleSaveStock = async () => {
    if (!isAdmin || !stockTarget) return;
    const qty = Number(stockInput);
    if (!Number.isFinite(qty) || qty < 0) {
      addNotification('จำนวนสต็อกไม่ถูกต้อง', 'warning');
      return;
    }
    try {
      const payload = {
        service_name: stockTarget.service_name,
        price: Number(stockTarget.price) || 0,
        commission_rate: Number(stockTarget.commission_rate) || 0,
        item_type: stockTarget.item_type || 'product',
        category_id: stockTarget.category_id || null,
        image_url: stockTarget.image_url || null,
        barcode: stockTarget.barcode || null,
        is_active: stockTarget.is_active !== false,
        stock_qty: qty,
      };
      const res = await api.put(`/services/${stockTarget.service_id}`, payload);
      setServices((prev) => prev.map((s) => (s.service_id === stockTarget.service_id ? res.data : s)));
      addNotification('ปรับปรุงสต็อกเรียบร้อย', 'success');
      closeStockModal();
    } catch (err) {
      addNotification(err.response?.data?.message || 'ปรับสต็อกไม่สำเร็จ', 'danger');
    }
  };

  const handleSubmitService = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!serviceForm.service_name.trim()) {
      addNotification("กรุณากรอกชื่อบริการ", "warning");
      return;
    }
    if (!serviceForm.price && serviceForm.price !== 0) {
      addNotification("กรุณากรอกราคา", "warning");
      return;
    }
    if (!serviceForm.commission_rate && serviceForm.commission_rate !== 0) {
      addNotification("กรุณากรอก % คอมมิชชั่น (ใส่ 0 ได้)", "warning");
      return;
    }
    try {
      const payload = {
        service_name: serviceForm.service_name.trim(),
        price: Number(serviceForm.price) || 0,
        commission_rate: Number(serviceForm.commission_rate) || 0,
        item_type: serviceForm.item_type || 'service',
        category_id: serviceForm.category_id || null,
        image_url: serviceForm.image_url || null,
        barcode: serviceForm.barcode || null,
        is_active: !!serviceForm.is_active,
      };
      if (payload.item_type === 'product') {
        payload.stock_qty = Number(serviceForm.stock_qty) || 0;
      }
      let res;
      if (isEditingService && serviceForm.service_id != null) {
        res = await api.put(`/services/${serviceForm.service_id}`, payload);
        setServices((prev) =>
          prev.map((s) =>
            s.service_id === serviceForm.service_id ? res.data : s
          )
        );
        addNotification("แก้ไขบริการสำเร็จ", "success");
      } else {
        res = await api.post("/services", payload);
        setServices((prev) => [...prev, res.data]);
        addNotification("เพิ่มบริการใหม่สำเร็จ", "success");
      }
      resetServiceForm();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกข้อมูลบริการไม่สำเร็จ",
        "danger"
      );
    }
  };

  const handleEditService = (svc) => {
    setServiceForm({
      service_id: svc.service_id,
      service_name: svc.service_name,
      price: svc.price,
      commission_rate: svc.commission_rate,
      item_type: svc.item_type || 'service',
      category_id: svc.category_id || "",
      image_url: svc.image_url || "",
      barcode: svc.barcode || "",
      is_active: svc.is_active !== false,
      stock_qty: svc.stock_qty ?? 0,
    });
    setIsEditingService(true);
    setIsServiceModalOpen(true);
  };

  const handleDeleteService = async (svc) => {
    if (!isAdmin) return;
    if (
      !window.confirm(
        `ต้องการลบบริการ "${svc.service_name}" หรือไม่?\nข้อมูลออเดอร์เก่าจะยังอยู่`
      )
    )
      return;
    try {
      await api.delete(`/services/${svc.service_id}`);
      setServices((prev) =>
        prev.filter((s) => s.service_id !== svc.service_id)
      );
      addNotification("ลบบริการสำเร็จ", "success");
    } catch (err) {
      addNotification(
        err.response?.data?.message || "ลบบริการไม่สำเร็จ",
        "danger"
      );
    }
  };

  const handleToggleActive = async (svc) => {
    if (!isAdmin) return;
    try {
      const payload = { is_active: !svc.is_active };
      const res = await api.put(`/services/${svc.service_id}/active`, payload);
      setServices((prev) =>
        prev.map((s) => (s.service_id === svc.service_id ? res.data : s))
      );
      addNotification(
        res.data.is_active ? "เปิดใช้งานบริการแล้ว" : "ปิดใช้งานบริการแล้ว",
        "success"
      );
    } catch (err) {
      addNotification(
        err.response?.data?.message || "เปลี่ยนสถานะบริการไม่สำเร็จ",
        "danger"
      );
    }
  };

  // Filter แสดงบริการ
  const servicesFiltered = useMemo(() => {
    return services
      .filter((s) =>
        activeCategoryFilter === "all"
          ? true
          : s.category_id === activeCategoryFilter
      )
      .filter((s) =>
        filterType === 'all' ? true : (filterType === 'service' ? (s.item_type !== 'product') : (s.item_type === 'product'))
      )
      .filter((s) =>
        filterStatus === 'all' ? true : (filterStatus === 'active' ? (s.is_active !== false) : (s.is_active === false))
      )
      .filter((s) => {
        if (filterStock === 'all') return true;
        const isProduct = (s.item_type || 'service') === 'product';
        if (!isProduct) return false;
        const remain = Number(s.stock_qty ?? 0);
        if (filterStock === 'low') return remain > 0 && remain <= LOW_STOCK;
        if (filterStock === 'out') return remain <= 0;
        return true;
      })
      .filter((s) =>
        searchText.trim()
          ? (s.service_name?.toLowerCase().includes(searchText.trim().toLowerCase())
            || (s.item_type || '').toLowerCase().includes(searchText.trim().toLowerCase())
            || (s.barcode || '').toLowerCase().includes(searchText.trim().toLowerCase()))
          : true
      )
      .sort((a, b) => {
        let va, vb;
        switch (sortBy) {
          case 'price': va = Number(a.price) || 0; vb = Number(b.price) || 0; break;
          case 'commission': va = Number(a.commission_rate) || 0; vb = Number(b.commission_rate) || 0; break;
          case 'stock': {
            const ra = (a.item_type === 'product') ? Number(a.stock_qty || 0) : -Infinity;
            const rb = (b.item_type === 'product') ? Number(b.stock_qty || 0) : -Infinity;
            va = ra; vb = rb; break;
          }
          case 'category': va = (getCategoryName(a.category_id) || ''); vb = (getCategoryName(b.category_id) || ''); break;
          case 'status': va = (a.is_active !== false) ? 1 : 0; vb = (b.is_active !== false) ? 1 : 0; break;
          case 'name':
          default: va = (a.service_name || ''); vb = (b.service_name || ''); break;
          case 'barcode': va = (a.barcode || ''); vb = (b.barcode || ''); break;
        }
        const comp = (typeof va === 'string' ? va.localeCompare(vb) : (va - vb));
        return sortDir === 'asc' ? comp : -comp;
      });
  }, [services, activeCategoryFilter, filterType, filterStatus, filterStock, searchText, sortBy, sortDir]);

  // Pagination
  const totalPages = useMemo(() => {
    return Math.ceil(servicesFiltered.length / pageSize);
  }, [servicesFiltered.length, pageSize]);

  const pagedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return servicesFiltered.slice(startIndex, startIndex + pageSize);
  }, [servicesFiltered, currentPage, pageSize]);

  const getCategoryName = (id) => {
    const cat = categories.find((c) => c.id === id);
    return cat ? cat.name : "-";
  };

  // Summary badges
  const totals = useMemo(() => {
    const total = services.length;
    const products = services.filter(s => (s.item_type || 'service') === 'product');
    const servicesOnly = total - products.length;
    const active = services.filter(s => s.is_active !== false).length;
    const inactive = total - active;
    const low = products.filter(p => Number(p.stock_qty || 0) > 0 && Number(p.stock_qty || 0) <= LOW_STOCK).length;
    const out = products.filter(p => Number(p.stock_qty || 0) <= 0).length;
    return { total, servicesOnly, products: products.length, active, inactive, low, out };
  }, [services]);

  const sortMark = (field) => (sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // UI
  return (
    <div className="dashboard-page services-page container">
      {/* hero */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-box-seam me-2"></i>จัดการสินค้า/บริการสำหรับ POS
          </div>
          <div className="dashboard-hero-subtitle">
            เพิ่มหมวดหมู่ จัดการราคา ค่าคอมมิชชั่น เปิด/ปิดใช้งาน และสต็อกสินค้าสำหรับ POS
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="card card-ks-soft">
        <div className="card-body">
          {/* Top Row: Button + Search */}
          <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-ks-success"
              onClick={() => {
                resetServiceForm();
                setIsServiceModalOpen(true);
              }}
              disabled={!isAdmin}
            >
              <i className="fas fa-plus me-1"></i>
              เพิ่มสินค้า/บริการ
            </button>

            <input
              className="form-control"
              style={{ maxWidth: 300 }}
              placeholder="ค้นหาชื่อ / Barcode..."
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
            />
          </div>

          {/* Filter Row: Dropdowns */}
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <label className="form-label mb-0 small text-muted">กรองข้อมูล:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={activeCategoryFilter}
              onChange={(e) => { setActiveCategoryFilter(e.target.value === "all" ? "all" : Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value="all">ทั้งหมด</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">ประเภททั้งหมด</option>
              <option value="service">บริการ</option>
              <option value="product">สินค้า</option>
            </select>

            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="active">สถานะเปิด</option>
              <option value="inactive">สถานะปิด</option>
            </select>

            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={filterStock}
              onChange={(e) => { setFilterStock(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">สต็อกทั้งหมด</option>
              <option value="low">สต็อกต่ำ (≤ {LOW_STOCK})</option>
              <option value="out">หมดสต็อก</option>
            </select>

            {isAdmin && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm ms-auto"
                onClick={() => {
                  resetCategoryForm();
                  setIsCategoryModalOpen(true);
                }}
              >
                <i className="bi bi-folder-plus me-1"></i>
                จัดการหมวดหมู่
              </button>
            )}
          </div>

          {/* Summary Tabs */}
          <div className="mb-3">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className={`btn btn-sm ${filterType === 'all' && filterStatus === 'all' && filterStock === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => { setFilterType('all'); setFilterStatus('all'); setFilterStock('all'); setCurrentPage(1); }}
              >
                ทั้งหมด {totals.total}
              </button>
              <button
                className={`btn btn-sm ${filterType === 'service' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setFilterType('service'); setCurrentPage(1); }}
              >
                บริการ {totals.servicesOnly}
              </button>
              <button
                className={`btn btn-sm ${filterType === 'product' ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => { setFilterType('product'); setCurrentPage(1); }}
              >
                สินค้า {totals.products}
              </button>
              <button
                className={`btn btn-sm ${filterStatus === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => { setFilterStatus('active'); setCurrentPage(1); }}
              >
                ใช้งาน {totals.active}
              </button>
              <button
                className={`btn btn-sm ${filterStatus === 'inactive' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => { setFilterStatus('inactive'); setCurrentPage(1); }}
              >
                ปิด {totals.inactive}
              </button>
              <button
                className={`btn btn-sm ${filterStock === 'out' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={() => { setFilterStock('out'); setCurrentPage(1); }}
              >
                หมดสต็อก {totals.out}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>บริการ/สินค้า{sortMark('name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('barcode')}>Barcode{sortMark('barcode')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('category')}>หมวดหมู่{sortMark('category')}</th>
                  <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('price')}>ราคา{sortMark('price')}</th>
                  <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('commission')}>% คอม{sortMark('commission')}</th>
                  <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('stock')}>คงเหลือ{sortMark('stock')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>สถานะ{sortMark('status')}</th>
                  <th style={{ width: 200 }}>ดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {pagedServices.map((svc) => (
                  <tr key={svc.service_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {svc.image_url && (
                          <img
                            src={svc.image_url}
                            alt={svc.service_name}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: "cover",
                              borderRadius: 6,
                              marginRight: 8,
                            }}
                          />
                        )}
                        <div>
                          <div className="fw-semibold">
                            {svc.service_name}
                          </div>
                          <div className="mt-1">
                            {svc.item_type === 'product' ? (
                              <span className="badge bg-info">สินค้า</span>
                            ) : (
                              <span className="badge bg-primary">บริการ</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {svc.barcode ? (
                        <span className="font-monospace text-muted small">{svc.barcode}</span>
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </td>
                    <td>{getCategoryName(svc.category_id)}</td>
                    <td className="text-end">
                      {formatMoney(svc.price)} ฿
                    </td>
                    <td className="text-end">
                      {svc.commission_rate}%
                    </td>
                    <td className="text-end">
                      {svc.item_type === 'product' ? (
                        <>
                          {Number(svc.stock_qty ?? 0)}{' '}
                          {Number(svc.stock_qty ?? 0) <= 0 ? (
                            <span className="badge bg-danger ms-1">หมด</span>
                          ) : Number(svc.stock_qty ?? 0) <= LOW_STOCK ? (
                            <span className="badge bg-warning text-dark ms-1">ต่ำ</span>
                          ) : null}
                        </>
                      ) : '-'}
                    </td>
                    <td>
                      {svc.is_active !== false ? (
                        <span className="badge bg-success">เปิดใช้งาน</span>
                      ) : (
                        <span className="badge bg-secondary">
                          ปิดใช้งาน
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-ks-outline btn-ks-icon"
                          onClick={() => handleEditService(svc)}
                          disabled={!isAdmin}
                          title="แก้ไข"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        {svc.item_type === 'product' && (
                          <button
                            type="button"
                            className="btn btn-outline-info btn-ks-outline btn-ks-icon"
                            onClick={() => openStockModal(svc)}
                            disabled={!isAdmin}
                            title="ปรับสต็อก"
                          >
                            <i className="bi bi-box-seam"></i>
                          </button>
                        )}
                        <button
                          type="button"
                          className={`btn btn-ks-outline ${svc.is_active !== false ? 'btn-outline-warning' : 'btn-outline-success'} btn-ks-icon`}
                          onClick={() => handleToggleActive(svc)}
                          disabled={!isAdmin}
                          title={svc.is_active !== false ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                        >
                          <i className={`bi ${svc.is_active !== false ? 'bi-toggle2-off' : 'bi-toggle2-on'}`}></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-ks-outline btn-ks-icon"
                          onClick={() => handleDeleteService(svc)}
                          disabled={!isAdmin}
                          title="ลบรายการ"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {servicesFiltered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-muted">
                      ยังไม่มีบริการที่ตรงกับเงื่อนไข
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex gap-2 align-items-center">
              <label className="form-label form-label-sm mb-0">
                แสดง:
              </label>
              <select
                className="form-select form-select-sm"
                style={{ width: "150px" }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={20}>20 รายการ</option>
                <option value={50}>50 รายการ</option>
                <option value={100}>100 รายการ</option>
              </select>
            </div>

            <div className="text-muted small">
              แสดง {servicesFiltered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} ถึง{" "}
              {Math.min(currentPage * pageSize, servicesFiltered.length)} จาก {servicesFiltered.length} รายการ
            </div>

            {/* Pagination */}
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    «
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Modal: เพิ่ม/แก้ไขบริการ */}
      {isServiceModalOpen && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditingService ? "แก้ไขรายการ" : "เพิ่มสินค้า/บริการ"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetServiceForm}
                ></button>
              </div>

              <form onSubmit={handleSubmitService}>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">
                        ชื่อสินค้า/บริการ <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        className="form-control form-control-sm"
                        value={serviceForm.service_name}
                        disabled={!isAdmin}
                        onChange={(e) =>
                          handleServiceFieldChange(
                            "service_name",
                            e.target.value
                          )
                        }
                        placeholder="เช่น สระ+ไดร์ / ทำสีผม"
                        autoFocus
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label form-label-sm">
                        ราคา (บาท) <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={serviceForm.price}
                        disabled={!isAdmin}
                        onChange={(e) =>
                          handleServiceFieldChange("price", e.target.value)
                        }
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label form-label-sm">
                        % คอมมิชชั่น <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={serviceForm.commission_rate}
                        disabled={!isAdmin}
                        onChange={(e) =>
                          handleServiceFieldChange(
                            "commission_rate",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label form-label-sm">
                        ประเภท
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={serviceForm.item_type}
                        disabled={!isAdmin}
                        onChange={(e) => handleServiceFieldChange('item_type', e.target.value)}
                      >
                        <option value="service">บริการ</option>
                        <option value="product">สินค้า</option>
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label form-label-sm">
                        Barcode / SKU
                      </label>
                      <input
                        className="form-control form-control-sm"
                        value={serviceForm.barcode}
                        disabled={!isAdmin}
                        onChange={(e) => handleServiceFieldChange('barcode', e.target.value)}
                        placeholder="รหัสบาร์โค้ด (ถ้ามี)"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label form-label-sm">
                        หมวดหมู่
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={serviceForm.category_id || ""}
                        disabled={!isAdmin}
                        onChange={(e) =>
                          handleServiceFieldChange(
                            "category_id",
                            e.target.value
                          )
                        }
                      >
                        <option value="">- ไม่ระบุ -</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-8">
                      <label className="form-label form-label-sm">
                        URL รูป (ใช้แสดงใน POS)
                      </label>
                      <input
                        className="form-control form-control-sm"
                        value={serviceForm.image_url}
                        disabled={!isAdmin}
                        onChange={(e) =>
                          handleServiceFieldChange("image_url", e.target.value)
                        }
                        placeholder="วางลิงก์รูป (ไม่บังคับ)"
                      />
                    </div>

                    {serviceForm.item_type === 'product' && (
                      <div className="col-md-4">
                        <label className="form-label form-label-sm">
                          จำนวนคงคลัง (เริ่มต้น/ปรับปรุง)
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={serviceForm.stock_qty ?? 0}
                          disabled={!isAdmin}
                          onChange={(e) => handleServiceFieldChange('stock_qty', e.target.value)}
                          min={0}
                        />
                      </div>
                    )}

                    <div className="col-md-4">
                      <label className="form-label form-label-sm">
                        สถานะ
                      </label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="svcActiveSwitch"
                          checked={serviceForm.is_active}
                          disabled={!isAdmin}
                          onChange={(e) =>
                            handleServiceFieldChange(
                              "is_active",
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label small"
                          htmlFor="svcActiveSwitch"
                        >
                          ใช้งานอยู่
                        </label>
                      </div>
                    </div>

                    {serviceForm.image_url && (
                      <div className="col-md-8">
                        <label className="form-label form-label-sm">
                          ตัวอย่างรูป
                        </label>
                        <div>
                          <img
                            src={serviceForm.image_url}
                            alt="preview"
                            style={{
                              maxHeight: 120,
                              maxWidth: "100%",
                              borderRadius: 8,
                              border: "1px solid #eee",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={resetServiceForm}
                  >
                    ยกเลิก
                  </button>
                  {isAdmin && (
                    <button
                      type="submit"
                      className="btn btn-ks-success btn-sm"
                    >
                      <i className="bi bi-save me-1"></i>
                      {isEditingService ? "บันทึกการแก้ไข" : "เพิ่มรายการ"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ปรับสต็อกสินค้า */}
      {isStockModalOpen && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">ปรับสต็อกสินค้า</h5>
                <button type="button" className="btn-close" onClick={closeStockModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <strong>สินค้า:</strong> {stockTarget?.service_name}
                  {stockTarget?.barcode && <span className="text-muted small ms-2">({stockTarget.barcode})</span>}
                </div>
                <div className="mb-3">
                  <label className="form-label form-label-sm">จำนวนคงคลังใหม่</label>
                  <input type="number" className="form-control form-control-sm" min={0} value={stockInput} onChange={(e) => setStockInput(e.target.value)} />
                  <div className="form-text">ตั้งค่าใหม่ทับจำนวนเดิม</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={closeStockModal}>ยกเลิก</button>
                <button type="button" className="btn btn-ks-success btn-sm" disabled={!isAdmin} onClick={handleSaveStock}>
                  <i className="bi bi-save me-1"></i>บันทึก
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCategoryModalOpen && (
        <ServiceCategoryManagerModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default ServicesPage;
